import * as React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface ComboboxOption {
  value: string;
  label: string;
  /** Shown to the right of the label, as the portal shows counts on its filters. */
  hint?: string | number;
  disabled?: boolean;
}

/** The value that means "do not filter by this at all". */
export const ALL_VALUE = '__all__';

/** Typing is only worth offering when there is enough to search through. */
const SEARCHABLE_FROM = 8;

/** Between the button and the list. */
const GAP = 4;
/** Kept clear of the window edge, so the list never sits flush against it. */
const EDGE = 8;
/** The list at its full height: the search row and about six options. */
const WANTED_HEIGHT = 336;
/** Below this the list is worth scrolling rather than flipping or shrinking further. */
const MIN_HEIGHT = 180;

/** Case- and punctuation-insensitive, so "1099 nec" finds "1099-NEC". */
function matches(option: ComboboxOption, needle: string): boolean {
  if (!needle) return true;
  const flatten = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '');
  return flatten(option.label).includes(flatten(needle));
}

/**
 * A filter the reader can type into, which is what the portal uses everywhere.
 *
 * A plain select is fine for three options and useless for forty: the form filter lists
 * every form Tab Service files, and finding 1099-PATR meant scrolling a list with no way to
 * narrow it. The portal replaced its selects with this and has no pills or plain selects
 * left, so the two products now behave the same way.
 *
 * Written against the DOM rather than a combobox library: the two packages the portal uses
 * for it both pull in a react-remove-scroll this workspace's lockfile cannot resolve, and a
 * filtered list with arrow keys is less code than that problem is worth.
 */
export function Combobox({
  value,
  onValueChange,
  options,
  placeholder = 'Choose',
  searchPlaceholder = 'Search…',
  emptyMessage = 'Nothing matches that.',
  className,
  id,
  ariaLabel,
  disabled,
}: {
  value: string | undefined;
  onValueChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [needle, setNeedle] = React.useState('');
  const [active, setActive] = React.useState(0);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const [place, setPlace] = React.useState<{
    host: HTMLElement;
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  const selected = options.find((option) => option.value === value);
  const searchable = options.length >= SEARCHABLE_FROM;
  const shown = React.useMemo(
    () => (searchable ? options.filter((option) => matches(option, needle)) : options),
    [options, needle, searchable],
  );

  // Opening lands on whatever is chosen, so the list starts where the reader left it.
  React.useEffect(() => {
    if (!open) return;
    setNeedle('');
    const at = options.findIndex((option) => option.value === value);
    setActive(at < 0 ? 0 : at);
    if (searchable) searchRef.current?.focus();
  }, [open, options, value, searchable]);

  // Clicking anywhere else closes it, the way every other menu in the app behaves. The
  // list lives outside the root (see below), so both have to be asked.
  React.useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  /*
   * Where the list goes, and where it is drawn from.
   *
   * It used to sit in the flow under the button. Inside a dialog that is inside the dialog's
   * scrolling body, so the list was clipped at the body's edge and what showed of it grew
   * the scrollbar - a two-option select could only be read by scrolling a dialog that had
   * nothing else to scroll. So it is lifted out of the flow and fixed to the button's edge.
   *
   * It is lifted into the nearest dialog rather than to the page, for two reasons. A dialog
   * traps focus inside itself, and would pull focus straight back out of a search box that
   * was not inside it; and a dialog is centred with a transform, which makes it the box a
   * fixed descendant is measured from - so the button's position is taken relative to it.
   * With no dialog around, the page is the host and the viewport is the box.
   *
   * It opens downwards where there is room and upwards where there is not, and takes only
   * the height the viewport has left either way. Opening downwards unconditionally at a
   * fixed height put the list off the bottom of the window whenever the button sat low on
   * the page - the form select near the foot of the upload step being the case that showed
   * it - and the options underneath could not be reached at all.
   */
  const measure = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const host = (trigger.closest('[role="dialog"]') as HTMLElement | null) ?? document.body;
    const rect = trigger.getBoundingClientRect();
    const origin = host === document.body ? { top: 0, left: 0 } : host.getBoundingClientRect();

    const below = window.innerHeight - rect.bottom - GAP - EDGE;
    const above = rect.top - GAP - EDGE;
    // Flip only when there is genuinely more room the other way, so the list stays below
    // the button in the ordinary case.
    const upwards = below < Math.min(WANTED_HEIGHT, MIN_HEIGHT) && above > below;
    const maxHeight = Math.max(MIN_HEIGHT, Math.min(WANTED_HEIGHT, upwards ? above : below));
    const top = upwards ? rect.top - origin.top - maxHeight - GAP : rect.bottom - origin.top + GAP;

    setPlace({ host, top, left: rect.left - origin.left, width: rect.width, maxHeight });
  }, []);

  React.useEffect(() => {
    if (!open) {
      setPlace(null);
      return;
    }
    measure();
    // Anything scrolling or resizing moves the button; the list follows it.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);

  React.useEffect(() => {
    if (!open) return;
    const item = listRef.current?.children[active] as HTMLElement | undefined;
    // Not every environment has it - jsdom does not - and keeping the active option in view
    // is a courtesy, not something worth throwing over.
    item?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  const choose = (option: ComboboxOption) => {
    if (option.disabled) return;
    onValueChange(option.value);
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => {
        if (shown.length === 0) return 0;
        return (current + step + shown.length) % shown.length;
      });
      return;
    }
    if (event.key === 'Enter' && open) {
      event.preventDefault();
      const option = shown[active];
      if (option) choose(option);
    }
  };

  return (
    <div ref={rootRef} className={cn('relative', className)} onKeyDown={onKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          'group inline-flex h-10 w-full items-center justify-between gap-2 rounded-md border border-[color:var(--ts-input)]',
          'bg-surface px-3 text-left text-base text-gray-3',
          'transition-[border-color,box-shadow,background-color] duration-150 ease-out',
          'hover:border-[color:var(--ts-input-hover)]',
          open && 'border-primary shadow-[0_0_0_3px_var(--ts-focus-ring-soft)]',
          'focus:border-primary focus:shadow-[0_0_0_3px_var(--ts-focus-ring-soft)] focus:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-60',
        )}
      >
        <span className={cn('truncate', !selected && 'text-[color:var(--ts-placeholder)]')}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-primary transition-transform duration-150 ease-out',
            open && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      {open && place
        ? createPortal(
        <div
          ref={popoverRef}
          role="presentation"
          onKeyDown={onKeyDown}
          // A press inside the list must not read as a press outside the dialog, or the
          // dialog closes under it.
          onPointerDown={(event) => event.stopPropagation()}
          style={{
            position: 'fixed',
            top: place.top,
            left: place.left,
            width: place.width,
            maxHeight: place.maxHeight,
          }}
          className={cn(
            'anim-pop-in z-50 flex min-w-[220px] flex-col overflow-hidden rounded-lg',
            'border border-[color:var(--ts-border-soft)] bg-surface shadow-float',
          )}
        >
          {searchable ? (
            <div className="flex shrink-0 items-center gap-2 border-b border-[color:var(--ts-border-soft)] px-3">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <input
                ref={searchRef}
                value={needle}
                onChange={(event) => {
                  setNeedle(event.target.value);
                  setActive(0);
                }}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="h-10 w-full bg-transparent text-base text-gray-3 placeholder:text-[color:var(--ts-placeholder)] focus:outline-none"
              />
            </div>
          ) : null}

          {shown.length === 0 ? (
            <p className="px-3 py-6 text-center text-base text-muted-foreground">{emptyMessage}</p>
          ) : (
            <ul ref={listRef} role="listbox" aria-label={ariaLabel} className="min-h-0 flex-1 overflow-y-auto p-1">
              {shown.map((option, index) => (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={option.value === value}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => choose(option)}
                  className={cn(
                    'flex cursor-pointer select-none items-center justify-between gap-2 rounded-md px-3 py-2 text-base text-gray-3',
                    index === active && 'bg-[color:var(--ts-row-hover)] text-primary',
                    option.disabled && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <Check
                      className={cn(
                        'h-4 w-4 shrink-0 text-primary',
                        option.value === value ? 'opacity-100' : 'opacity-0',
                      )}
                      aria-hidden="true"
                    />
                    <span className="truncate">{option.label}</span>
                  </span>
                  {option.hint !== undefined ? (
                    <span className="shrink-0 text-sm tabular-nums text-muted-foreground">{option.hint}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>,
        place.host,
          )
        : null}
    </div>
  );
}
